import React, { useState } from 'react';
import { Music, File, Download, Play, Pause, Volume2, Settings, AlertCircle, CheckCircle, Upload, Wand2 } from 'lucide-react';
import FileImport from './components/FileImport';
import SheetMusicViewer from './components/SheetMusicViewer';

// Tongan Music Converter Class
class TonganMusicConverter {
  constructor() {
    this.scaleDegrees = { 1: '3', 2: '4', 3: '5', 4: '6', 5: '7', 6: '8', 7: '9' };
    this.chromaticDegrees = { '1#': '3*', '2#': '4*', '4#': '6*', '5#': '7*', '6#': '8*' };
    this.keySignatures = {
      'C': { tonic: 'C', sharps: 0, flats: 0 }, 'G': { tonic: 'G', sharps: 1, flats: 0 },
      'D': { tonic: 'D', sharps: 2, flats: 0 }, 'A': { tonic: 'A', sharps: 3, flats: 0 },
      'E': { tonic: 'E', sharps: 4, flats: 0 }, 'B': { tonic: 'B', sharps: 5, flats: 0 },
      'F#': { tonic: 'F#', sharps: 6, flats: 0 }, 'F': { tonic: 'F', sharps: 0, flats: 1 },
      'Bb': { tonic: 'Bb', sharps: 0, flats: 2 }, 'Eb': { tonic: 'Eb', sharps: 0, flats: 3 },
      'Ab': { tonic: 'Ab', sharps: 0, flats: 4 }, 'Db': { tonic: 'Db', sharps: 0, flats: 5 },
      'Gb': { tonic: 'Gb', sharps: 0, flats: 6 }
    };
    this.chromaticScale = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    this.voiceRanges = {
      'fasi': { baseOctave: 4, min: 3, max: 5 },
      'kanokano': { baseOctave: 4, min: 3, max: 5 },
      'tenoa': { baseOctave: 3, min: 2, max: 4 },
      'laulalo': { baseOctave: 2, min: 1, max: 3 }
    };
  }

  // Get scale degree from pitch and key
  getScaleDegree(pitch, key) {
    const pitchClass = pitch.replace(/\d+/, ''); // Remove octave
    const keyTonic = this.keySignatures[key]?.tonic || 'C';

    const getSemitoneName = (pc) => {
      const flatToSharp = { 'Bb': 'A#', 'Eb': 'D#', 'Ab': 'G#', 'Db': 'C#', 'Gb': 'F#' };
      return flatToSharp[pc] || pc;
    };
    
    // Get semitone positions
    const pitchIndex = this.chromaticScale.indexOf(getSemitoneName(pitchClass));
    const tonicIndex = this.chromaticScale.indexOf(getSemitoneName(keyTonic));
    
    if (pitchIndex === -1 || tonicIndex === -1) return '?';
    
    // Calculate interval from tonic
    let interval = (pitchIndex - tonicIndex + 12) % 12;
    
    // Map to scale degrees
    const degreeMap = {
      0: '3',   // Tonic = Do
      2: '4',   // Major 2nd = Re
      4: '5',   // Major 3rd = Mi
      5: '6',   // Perfect 4th = Fa
      7: '7',   // Perfect 5th = Sol
      9: '8',   // Major 6th = La
      11: '9',  // Major 7th = Ti
      // Chromatic degrees with strikethrough
      1: '3*',  // C#/Db
      3: '4*',  // D#/Eb
      6: '6*',  // F#/Gb
      8: '7*',  // G#/Ab
      10: '8*'  // A#/Bb
    };
    
    return degreeMap[interval] || '?';
  }

  // Convert duration to beat count for Tongan notation
  durationToBeats(duration, timeSignature) {
    const [beats, beatType] = timeSignature.split('/').map(Number);
    const beatUnit = 4 / beatType; // Quarter note = 1 in 4/4 time
    
    const durationMap = {
      'whole': 4 * beatUnit,
      'half': 2 * beatUnit,
      'quarter': 1 * beatUnit,
      'eighth': 0.5 * beatUnit,
      'sixteenth': 0.25 * beatUnit,
      'dotted-whole': 6 * beatUnit,
      'dotted-half': 3 * beatUnit,
      'dotted-quarter': 1.5 * beatUnit,
      'dotted-eighth': 0.75 * beatUnit
    };
    
    return durationMap[duration] || 1;
  }

  // Convert a measure of notes to Tongan notation
  measureToTongan(notes, key, timeSignature, isPickupMeasure = false) {
    if (!notes || notes.length === 0) return '0';
    
    const [beatsPerMeasure, beatType] = timeSignature.split('/').map(Number);
    
    // Determine beat unit for grouping
    // In compound time (6/8, 9/8, 12/8), group by 3 sub-beats
    const isCompoundTime = beatType === 8 && beatsPerMeasure % 3 === 0;
    const groupSize = isCompoundTime ? 3 : 1; // Group every 3 eighths in 6/8
    
    let result = [];
    let currentBeatGroup = '';
    let subBeatCount = 0;
    
    notes.forEach(note => {
      const beats = this.durationToBeats(note.duration, timeSignature);
      
      if (note.isRest) {
        if (beats >= 1) {
          if (currentBeatGroup) {
            result.push(currentBeatGroup);
            currentBeatGroup = '';
            subBeatCount = 0;
          }
          const dashes = '-'.repeat(Math.floor(beats) - 1);
          result.push('0' + dashes);
        } else {
          currentBeatGroup += '0';
          subBeatCount++;
        }
      } else {
        const degree = this.getScaleDegree(note.pitch, key);
        
        if (beats >= 1) {
          if (currentBeatGroup) {
            result.push(currentBeatGroup);
            currentBeatGroup = '';
            subBeatCount = 0;
          }
          const dashes = '-'.repeat(Math.floor(beats) - 1);
          result.push(degree + dashes);
        } else {
          currentBeatGroup += degree;
          subBeatCount++;
          
          // Group by beat (every 2 eighths in 4/4, every 3 eighths in 6/8)
          const notesPerBeat = isCompoundTime ? 3 : 2;
          if (subBeatCount >= notesPerBeat) {
            result.push(currentBeatGroup);
            currentBeatGroup = '';
            subBeatCount = 0;
          }
        }
      }
    });
    
    if (currentBeatGroup) {
      result.push(currentBeatGroup);
    }
    
    return result.join(':');
  }

  formatTonganDisplay(tonganSong, includeLyrics = false) {
    const { key, timeSignature, fasi, kanokano, tenoa, laulalo, lyrics } = tonganSong;
    let output = `Key: ${key} ${timeSignature}\n\n`;
    const maxMeasures = Math.max(
      fasi?.length || 0, kanokano?.length || 0, tenoa?.length || 0, laulalo?.length || 0
    );
    
    for (let i = 0; i < maxMeasures; i++) {
      output += `Measure ${i + 1}:\n`;
      if (fasi && fasi[i]) output += `Fasi:     |${fasi[i]}|\n`;
      if (kanokano && kanokano[i]) output += `Kānokano: |${kanokano[i]}|\n`;
      if (includeLyrics && lyrics && lyrics[i]) output += `Lyrics:   ${lyrics[i]}\n`;
      if (tenoa && tenoa[i]) output += `Tēnoa:    |${tenoa[i]}|\n`;
      if (laulalo && laulalo[i]) output += `Laulalo:  |${laulalo[i]}|\n`;
      output += '\n';
    }
    return output;
  }

  formatTonganStaff(tonganSong, measuresPerLine = 7) {
    const { key, timeSignature, fasi, kanokano, tenoa, laulalo } = tonganSong;
    let output = `Key: ${key} ${timeSignature}\n\n`;
    
    const parts = [];
    if (fasi?.length > 0 && fasi.some(m => m && m.trim())) {
      parts.push({ name: 'Fasi:    ', data: fasi });
    }
    if (kanokano?.length > 0 && kanokano.some(m => m && m.trim())) {
      parts.push({ name: 'Kānokano:', data: kanokano });
    }
    if (tenoa?.length > 0 && tenoa.some(m => m && m.trim())) {
      parts.push({ name: 'Tēnoa:   ', data: tenoa });
    }
    if (laulalo?.length > 0 && laulalo.some(m => m && m.trim())) {
      parts.push({ name: 'Laulalo: ', data: laulalo });
    }
    
    if (parts.length === 0) {
      return output + 'No musical data to display';
    }
    
    const maxMeasures = Math.max(...parts.map(p => p.data.length));
    
    for (let lineStart = 0; lineStart < maxMeasures; lineStart += measuresPerLine) {
      const lineEnd = Math.min(lineStart + measuresPerLine, maxMeasures);
      
      // Calculate max width for each measure in this line
      const measureWidths = [];
      for (let i = lineStart; i < lineEnd; i++) {
        let maxWidth = 0;
        parts.forEach(part => {
          const measure = part.data[i] || '';
          maxWidth = Math.max(maxWidth, measure.length);
        });
        measureWidths.push(maxWidth);
      }
      
      // Build each part's line with padding
      parts.forEach(part => {
        let measures = '';
        for (let i = lineStart; i < lineEnd; i++) {
          const measure = part.data[i] || '';
          const paddedMeasure = measure.padEnd(measureWidths[i - lineStart], ' ');
          measures += `|${paddedMeasure}`;
        }
        measures += '|';
        output += `${part.name} ${measures}\n`;
      });
      
      output += '\n';
    }
    
    return output;
  }
}

// Simple audio player using Web Audio API
class TonganAudioPlayer {
  constructor() {
    this.audioContext = null;
    this.isPlaying = false;
  }

  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
  }

  stop() {
    this.isPlaying = false;
    if (this.audioContext && this.audioContext.state === 'running') {
      this.audioContext.suspend();
    }
  }

  // Convert Tongan scale degree to frequency
  degreeToFrequency(degree, key, octave = 4) {
    const noteMap = {
      '3': 'C', '4': 'D', '5': 'E', '6': 'F',
      '7': 'G', '8': 'A', '9': 'B',
      '3*': 'C#', '4*': 'D#', '6*': 'F#', '7*': 'G#', '8*': 'A#'
    };
    
    const keyOffsets = {
      'C': 0, 'G': 7, 'D': 2, 'A': 9, 'E': 4, 'B': 11,
      'F#': 6, 'F': 5, 'Bb': 10, 'Eb': 3, 'Ab': 8, 'Db': 1, 'Gb': 6
    };
    
    const note = noteMap[degree] || 'C';
    const keyOffset = keyOffsets[key] || 0;
    
    // A4 = 440Hz reference
    const A4 = 440;
    const semitoneMap = {
      'C': -9, 'C#': -8, 'D': -7, 'D#': -6, 'E': -5, 'F': -4,
      'F#': -3, 'G': -2, 'G#': -1, 'A': 0, 'A#': 1, 'B': 2
    };
    
    const semitones = semitoneMap[note] + (octave - 4) * 12;
    return A4 * Math.pow(2, semitones / 12);
  }

  playNote(frequency, duration, time) {
    this.init();
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, time);
    gainNode.gain.exponentialRampToValueAtTime(0.01, time + duration);
    
    oscillator.start(time);
    oscillator.stop(time + duration);
  }

  async playTonganSong(tonganSong, selectedParts, key, tempo = 100, setIsPlayingCb) {
    this.init();
    if (this.audioContext) {
      await this.audioContext.resume();
    }
    this.isPlaying = true;
    if (setIsPlayingCb) setIsPlayingCb(true);
    
    const beatDuration = 60 / tempo; // seconds per beat
    let currentTime = this.audioContext.currentTime + 0.1; // Small delay to start
    
    const timeSig = tonganSong.timeSignature || '4/4';
    const [beatsPerMeasureStr] = timeSig.split('/');
    const beatsPerMeasureNum = parseInt(beatsPerMeasureStr) || 4;
    
    const parts = {
      fasi: { notes: tonganSong.fasi, octave: 5 },
      kanokano: { notes: tonganSong.kanokano, octave: 4 },
      tenoa: { notes: tonganSong.tenoa, octave: 4 },
      laulalo: { notes: tonganSong.laulalo, octave: 3 }
    };
    
    // Get max measures
    const maxMeasures = Math.max(
      ...Object.values(parts).map(p => p.notes?.length || 0)
    );
    
    console.log(`Playing ${maxMeasures} measures`);
    
    for (let measureIndex = 0; measureIndex < maxMeasures; measureIndex++) {
      const measureStartTime = currentTime;
      
      selectedParts.forEach(partName => {
        const part = parts[partName];
        if (!part || !part.notes) return;
        
        const measure = part.notes[measureIndex];
        if (!measure || measure.trim() === '') return;
        
        let beatTime = measureStartTime;
        
        // Parse measure notation - split by colons for beats
        const beats = measure.split(':');
        
        beats.forEach(beat => {
          if (!beat || beat.trim() === '') return;
          
          if (beat === '0' || beat.match(/^0-+$/)) {
            // Rest - count dashes for duration
            const duration = (beat.match(/-/g)?.length || 0) + 1;
            beatTime += duration * beatDuration;
          } else if (beat.match(/^[3-9]\*?-+$/)) {
            // Single tied note (e.g., "3--" or "7*-")
            const noteMatch = beat.match(/^([3-9]\*?)/);
            if (noteMatch) {
              let note = noteMatch[1].replace(/̶/g, '*');
              const duration = (beat.match(/-/g)?.length || 0) + 1;
              const freq = this.degreeToFrequency(note, key, part.octave);
              this.playNote(freq, duration * beatDuration * 0.95, beatTime);
              beatTime += duration * beatDuration;
            }
          } else if (beat.match(/^[3-9\*]+$/)) {
            // Multiple notes in one beat (eighths/sixteenths)
            const notes = beat.match(/[3-9]\*?/g) || [];
            const noteDuration = beatDuration / notes.length;
            notes.forEach(note => {
              let cleanNote = note.replace(/̶/g, '*');
              const freq = this.degreeToFrequency(cleanNote, key, part.octave);
              this.playNote(freq, noteDuration * 0.9, beatTime);
              beatTime += noteDuration;
            });
          } else {
            // Fallback: single note
            const noteMatch = beat.match(/^([3-9]\*?)/);
            if (noteMatch) {
              let note = noteMatch[1].replace(/̶/g, '*');
              const freq = this.degreeToFrequency(note, key, part.octave);
              this.playNote(freq, beatDuration * 0.95, beatTime);
            }
            beatTime += beatDuration;
          }
        });
      });
      
      // Move to next measure
      currentTime = measureStartTime + (beatsPerMeasureNum * beatDuration);
    }
    
    // Reset playing state after song finishes
    const totalDuration = (currentTime - this.audioContext.currentTime) * 1000;
    console.log(`Total song duration: ${totalDuration}ms`);
    
    setTimeout(() => {
      this.isPlaying = false;
      if (setIsPlayingCb) setIsPlayingCb(false);
    }, Math.max(0, totalDuration));
  }
}

// Main App Component
const TongaMusikaApp = () => {
  const [activeTab, setActiveTab] = useState('convert');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(true);
  const [converter] = useState(new TonganMusicConverter());
  
  // State for imported music
  const [importedMusicXML, setImportedMusicXML] = useState(null);
  const [importedMusicData, setImportedMusicData] = useState(null);
  
  // Conversion states
  const [conversionMode, setConversionMode] = useState('western-to-tongan');
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [selectedKey, setSelectedKey] = useState('C');
  const [selectedTimeSignature, setSelectedTimeSignature] = useState('4/4');
  
  // Audio states
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedVoiceParts, setSelectedVoiceParts] = useState(['fasi', 'kanokano', 'tenoa', 'laulalo']);
  const [audioInstrument, setAudioInstrument] = useState('piano');
  
  // AI Composer states
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  const keys = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb'];
  const timeSignatures = ['4/4', '3/4', '2/4', '6/8', '12/8', '2/2', '3/2'];
  const voiceParts = [
    { id: 'fasi', label: 'Fasi (Soprano)', color: 'bg-blue-500' },
    { id: 'kanokano', label: 'Kānokano (Alto)', color: 'bg-green-500' },
    { id: 'tenoa', label: 'Tēnoa (Tenor)', color: 'bg-yellow-500' },
    { id: 'laulalo', label: 'Laulalo (Bass)', color: 'bg-red-500' }
  ];

  const [audioPlayer] = useState(new TonganAudioPlayer());

  const handleAudioPlay = () => {
    if (isPlaying) {
      audioPlayer.stop();
      setIsPlaying(false);
    } else {
      if (!outputText || !outputText.includes('Key:')) {
        alert('Please convert music first');
        return;
      }
      
      const lines = outputText.split('\n').filter(l => l.trim());
      const keyLine = lines.find(l => l.startsWith('Key:'));
      const [, keyAndTime] = keyLine.split('Key:');
      const [key] = keyAndTime.trim().split(' ');
      
      // Extract all measures for each part
      const tonganSong = {
        fasi: [],
        kanokano: [],
        tenoa: [],
        laulalo: []
      };
      
      lines.forEach(line => {
        const extractMeasures = (partName) => {
          if (line.includes(partName)) {
            // Extract everything between | pipes
            const measureMatches = line.match(/\|([^|]+)\|/g);
            if (measureMatches) {
              return measureMatches.map(m => m.replace(/\|/g, '').trim()).filter(m => m);
            }
          }
          return null;
        };
        
        const fasiMeasures = extractMeasures('Fasi:');
        const kanokanoMeasures = extractMeasures('Kānokano:');
        const tenoaMeasures = extractMeasures('Tēnoa:');
        const laulaloMeasures = extractMeasures('Laulalo:');
        
        if (fasiMeasures) tonganSong.fasi.push(...fasiMeasures);
        if (kanokanoMeasures) tonganSong.kanokano.push(...kanokanoMeasures);
        if (tenoaMeasures) tonganSong.tenoa.push(...tenoaMeasures);
        if (laulaloMeasures) tonganSong.laulalo.push(...laulaloMeasures);
      });
      
      console.log('Playing song:', tonganSong);
      console.log('Selected parts:', selectedVoiceParts);
      console.log('Key:', key);
      
      audioPlayer.playTonganSong(tonganSong, selectedVoiceParts, key, 100, setIsPlaying);
    }
  };

  /**
   * Parse duration code
   */
  const parseDuration = (code) => {
    const map = {
      'w': 'whole',
      'h': 'half',
      'q': 'quarter',
      'e': 'eighth',
      's': 'sixteenth'
    };
    return map[code] || 'quarter';
  };

  /**
   * Parse measure notes from string
   */
  const parseMeasureNotes = (measureStr) => {
    const notes = [];
    const tokens = measureStr.split(/\s+/);

    tokens.forEach(token => {
      if (!token) return;

      // Handle rest notation: R(q), R(h), etc.
      if (token.toUpperCase().startsWith('R')) {
        const durationMatch = token.match(/\(([qhwes])\)/);
        const duration = durationMatch ? parseDuration(durationMatch[1]) : 'quarter';
        notes.push({
          pitch: null,
          duration: duration,
          isRest: true
        });
        return;
      }

      // Parse note: C4(q), D#4(h), Bb3, etc.
      const noteMatch = token.match(/^([A-G][#b]?)(\d)(\(([qhwes])\))?$/);
      if (noteMatch) {
        const [, step, octave, , durationCode] = noteMatch;
        const pitch = step + octave;
        const duration = durationCode ? parseDuration(durationCode) : 'quarter';

        notes.push({
          pitch: pitch,
          duration: duration,
          isRest: false
        });
      }
    });

    return notes;
  };

  /**
   * Parse four-part harmony input
   */
  const parseFourPartHarmony = (lines, key, timeSignature) => {
    const parts = {
      fasi: [],
      kanokano: [],
      tenoa: [],
      laulalo: []
    };

    lines.forEach(line => {
      let voicePart = null;
      let content = line;

      if (line.toLowerCase().startsWith('fasi:')) {
        voicePart = 'fasi';
        content = line.substring(5).trim();
      } else if (line.toLowerCase().startsWith('kanokano:')) {
        voicePart = 'kanokano';
        content = line.substring(9).trim();
      } else if (line.toLowerCase().startsWith('tenoa:')) {
        voicePart = 'tenoa';
        content = line.substring(6).trim();
      } else if (line.toLowerCase().startsWith('laulalo:')) {
        voicePart = 'laulalo';
        content = line.substring(8).trim();
      }

      if (voicePart) {
        const measures = content.split('|').map(m => m.trim()).filter(m => m);
        measures.forEach(measureStr => {
          const notes = parseMeasureNotes(measureStr);
          parts[voicePart].push(notes);
        });
      }
    });

    return parts;
  };

  /**
   * Parse single line input
   */
  const parseSingleLine = (input, key, timeSignature) => {
    const measureStrings = input.split('|').map(m => m.trim()).filter(m => m);
    
    const allParts = {
      fasi: [],
      kanokano: [],
      tenoa: [],
      laulalo: []
    };

    measureStrings.forEach(measureStr => {
      const notes = parseMeasureNotes(measureStr);
      // Single line input goes to all parts
      allParts.fasi.push(notes);
      allParts.kanokano.push(notes);
      allParts.tenoa.push(notes);
      allParts.laulalo.push(notes);
    });

    return allParts;
  };

  /**
   * Parse Western notation input
   */
  const parseWesternInput = (input, key, timeSignature) => {
    const lines = input.split('\n').filter(line => line.trim());
    const isFourPart = lines.some(line => 
      line.toLowerCase().startsWith('fasi:') || 
      line.toLowerCase().startsWith('kanokano:') ||
      line.toLowerCase().startsWith('tenoa:') ||
      line.toLowerCase().startsWith('laulalo:')
    );

    if (isFourPart) {
      return parseFourPartHarmony(lines, key, timeSignature);
    } else {
      return parseSingleLine(input, key, timeSignature);
    }
  };

  /**
   * Extract key from MusicXML fifths value
   */
  const fifthsToKey = (fifths) => {
    const keyMap = {
      '-7': 'Cb', '-6': 'Gb', '-5': 'Db', '-4': 'Ab', 
      '-3': 'Eb', '-2': 'Bb', '-1': 'F', '0': 'C',
      '1': 'G', '2': 'D', '3': 'A', '4': 'E', 
      '5': 'B', '6': 'F#', '7': 'C#'
    };
    return keyMap[fifths.toString()] || 'C';
  };

  /**
   * Convert MusicXML data to Tongan notation
   */
  const convertMusicXMLToTongan = (musicData) => {
    let key = 'C';
    let timeSignature = '4/4';

    // Extract key and time signature
    if (musicData.parts && musicData.parts.length > 0) {
      const firstPart = musicData.parts[0];
      if (firstPart.measures && firstPart.measures.length > 0) {
        const firstMeasure = firstPart.measures[0];
        
        if (firstMeasure.key !== undefined) {
          key = fifthsToKey(parseInt(firstMeasure.key));
        }
        
        if (firstMeasure.timeSignature) {
          timeSignature = firstMeasure.timeSignature;
        }
      }
    }

    const tonganParts = {
      fasi: [],
      kanokano: [],
      tenoa: [],
      laulalo: []
    };

    // Improved voice mapping logic
    const partCount = musicData.parts.length;
    
    musicData.parts.forEach((part, partIndex) => {
      let voicePart;
      
      if (partCount === 2) {
        // 2 parts = Treble staff (Fasi) and Bass staff (Tēnoa or Laulalo)
        voicePart = partIndex === 0 ? 'fasi' : 'tenoa';
      } else if (partCount === 4) {
        // 4 parts = SATB
        const voiceMap = ['fasi', 'kanokano', 'tenoa', 'laulalo'];
        voicePart = voiceMap[partIndex] || 'fasi';
      } else {
        // Fallback: try to identify by part name
        const partName = part.name?.toLowerCase() || '';
        
        if (partName.includes('soprano') || partName.includes('fasi') || partIndex === 0) {
          voicePart = 'fasi';
        } else if (partName.includes('alto') || partName.includes('kanokano') || partName.includes('voice 2')) {
          voicePart = 'kanokano';
        } else if (partName.includes('tenor') || partName.includes('tenoa')) {
          voicePart = 'tenoa';
        } else if (partName.includes('bass') || partName.includes('laulalo')) {
          voicePart = 'laulalo';
        } else {
          // Default based on index
          const voiceMap = ['fasi', 'kanokano', 'tenoa', 'laulalo'];
          voicePart = voiceMap[partIndex % 4];
        }
      }
      
      console.log(`Mapping part ${partIndex} (${part.name}) to ${voicePart}`);
      
      part.measures.forEach(measure => {
        const notes = measure.notes.map(note => ({
          pitch: note.isRest ? null : `${note.step}${note.octave}`,
          duration: note.type || 'quarter',
          isRest: note.isRest
        }));
        
        const tonganMeasure = converter.measureToTongan(notes, key, timeSignature);
        tonganParts[voicePart].push(tonganMeasure);
      });
    });

    return {
      key,
      timeSignature,
      title: musicData.title,
      composer: musicData.composer,
      ...tonganParts
    };
  };

  /**
   * Analyze MusicXML file
   */
  const analyzeMusicXMLFile = (musicData) => {
    console.log('=== MusicXML Analysis ===');
    console.log('Title:', musicData.title);
    console.log('Composer:', musicData.composer);
    console.log('Parts:', musicData.parts?.length || 0);
    
    if (musicData.parts) {
      musicData.parts.forEach((part, i) => {
        console.log(`\nPart ${i + 1}: ${part.name}`);
        console.log(`  Measures: ${part.measures?.length || 0}`);
        
        if (part.measures && part.measures[0]) {
          console.log(`  First measure key: ${part.measures[0].key || 'not specified'}`);
          console.log(`  First measure time: ${part.measures[0].timeSignature || 'not specified'}`);
          console.log(`  First measure notes: ${part.measures[0].notes?.length || 0}`);
        }
      });
    }
    
    return musicData;
  };

  /**
   * Handle music file import
   */
  const handleMusicDataParsed = (musicData) => {
    console.log('Received music data:', musicData);
    setImportedMusicData(musicData);
    analyzeMusicXMLFile(musicData);
    
    if (musicData.format === 'musicxml') {
      // Extract and set key/time signature
      if (musicData.parts && musicData.parts.length > 0) {
        const firstPart = musicData.parts[0];
        if (firstPart.measures && firstPart.measures.length > 0) {
          const firstMeasure = firstPart.measures[0];
          
          if (firstMeasure.key !== undefined) {
            const extractedKey = fifthsToKey(parseInt(firstMeasure.key));
            setSelectedKey(extractedKey);
          }
          
          if (firstMeasure.timeSignature) {
            setSelectedTimeSignature(firstMeasure.timeSignature);
          }
        }
      }
      
      // Auto-convert to Tongan
      handleConvertFromImport(musicData);
    } else {
      setOutputText(musicData.message || 'File processed. Please convert to MusicXML for best results.');
    }
  };

  /**
   * Handle import error
   */
  const handleImportError = (error) => {
    setOutputText(`Import Error: ${error.message}\n\nPlease ensure you're uploading a valid MusicXML, PDF, or image file.`);
  };

  /**
   * Convert imported music data to Tongan
   */
  const handleConvertFromImport = (musicData) => {
    try {
      const tonganSong = convertMusicXMLToTongan(musicData);
      const maxTongan = Math.max(
        tonganSong.fasi?.length || 0,
        tonganSong.kanokano?.length || 0,
        tonganSong.tenoa?.length || 0,
        tonganSong.laulalo?.length || 0
      );
      const sourceMeasures = musicData.parts?.[0]?.measures?.length || 0;
      console.log('Source measures:', sourceMeasures);
      console.log('Tongan measures:', maxTongan);
      if (sourceMeasures !== maxTongan) {
        console.warn(`⚠️ Measure count mismatch: Source has ${sourceMeasures}, output has ${maxTongan}`);
      } else {
        console.log('✓ Measure counts match');
      }
      const formatted = converter.formatTonganStaff(tonganSong, 7);
      setOutputText(`✓ Successfully converted to Tu'ungafasi notation!\n\n${formatted}`);
    } catch (error) {
      console.error('Conversion error:', error);
      setOutputText(`Conversion Error: ${error.message}`);
    }
  };
  
  /**
   * Handle manual conversion
   */
  const handleConvert = () => {
    try {
      if (conversionMode === 'western-to-tongan') {
        const input = inputText.trim();
        
        if (!input) {
          setOutputText('Please enter Western notation');
          return;
        }

        // Parse the input
        const parsedParts = parseWesternInput(input, selectedKey, selectedTimeSignature);
        
        // Convert each part to Tongan
        const tonganSong = {
          key: selectedKey,
          timeSignature: selectedTimeSignature,
          fasi: parsedParts.fasi.map(notes => converter.measureToTongan(notes, selectedKey, selectedTimeSignature)),
          kanokano: parsedParts.kanokano.map(notes => converter.measureToTongan(notes, selectedKey, selectedTimeSignature)),
          tenoa: parsedParts.tenoa.map(notes => converter.measureToTongan(notes, selectedKey, selectedTimeSignature)),
          laulalo: parsedParts.laulalo.map(notes => converter.measureToTongan(notes, selectedKey, selectedTimeSignature))
        };
        
        const result = converter.formatTonganStaff(tonganSong, 7);
        setOutputText(result);
        
      } else {
        setOutputText('Tongan to Western conversion - coming soon!');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setOutputText(`Error: ${error.message}`);
    }
  };

  const handleExport = (format) => {
    // Changed validation - check for Key: instead of Measure
    if (!outputText || !outputText.includes('Key:')) {
      alert('Please convert music first before exporting');
      return;
    }
    
    switch(format) {
      case 'txt':
        exportAsText();
        break;
      case 'pdf':
        exportAsPDF();
        break;
      case 'xml':
      case 'mxl':
        alert('MusicXML export coming soon! For now, use the text export.');
        exportAsText();
        break;
      default:
        exportAsText();
    }
  };

  const exportAsText = () => {
    const filename = `tonga-musika-${Date.now()}.txt`;
    const blob = new Blob([outputText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAsPDF = () => {
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Tonga Musika Export</title>
          <style>
            body { font-family: 'Courier New', monospace; padding: 40px; }
            h1 { color: #7c3aed; }
            pre { white-space: pre-wrap; line-height: 1.6; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>Tonga Musika - Tu'ungafasi Notation</h1>
          <pre>${outputText}</pre>
          <script>window.print();</script>
        </body>
      </html>
    `);
  };

  const handleAIGenerate = async () => {
    setAiGenerating(true);
    setTimeout(() => {
      const generatedSong = {
        key: selectedKey,
        timeSignature: selectedTimeSignature,
        fasi: ['3:4:5:6', '7:8:9:7', '8:7:6:5', '4:3:3:3'],
        kanokano: ['3:3:5:5', '7:7:7:5', '6:5:4:3', '4:3:3:3'],
        tenoa: ['3:4:5:6', '5:6:7:5', '6:5:4:3', '4:3:3:3'],
        laulalo: ['3:3:3:3', '7:7:7:7', '6:6:6:6', '3:3:3:3'],
        lyrics: ['Ko e ʻOtua ʻoku mau lotu', 'Ki he huafa ʻo Sisu', 'Ko e ʻEiki ʻoku mālohi', 'ʻOku ne ʻofa mai']
      };
      const formatted = converter.formatTonganDisplay(generatedSong, true);
      setOutputText(formatted);
      setAiGenerating(false);
    }, 2000);
  };

  if (showTerms && !termsAccepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8">
          <div className="flex items-center gap-3 mb-6">
            <Music className="w-10 h-10 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-800">Tonga Musika</h1>
          </div>
          
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Terms & Conditions</h2>
            <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto border border-gray-200">
              <p className="text-sm text-gray-700 mb-4"><strong>Development Project Notice</strong></p>
              <p className="text-sm text-gray-700 mb-4">
                Tonga Musika is a development project designed to preserve and promote Tongan musical heritage through digital conversion tools. By using this platform, you agree to the following terms:
              </p>
              
              <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                <li><strong>Copyright Compliance:</strong> You must ensure all musical content uploaded or created complies with relevant copyright laws. You are solely responsible for obtaining necessary permissions for copyrighted material.</li>
                <li><strong>Indemnification:</strong> You agree to indemnify and hold harmless Tonga Musika, its developers, and associated parties from any claims, damages, or legal actions arising from your use of copyrighted material without proper authorization.</li>
                <li><strong>Educational & Cultural Purpose:</strong> This tool is provided for educational, cultural preservation, and non-commercial purposes. Commercial use requires explicit written permission.</li>
                <li><strong>Data Privacy:</strong> Musical compositions created or converted using this platform may be stored for improvement purposes. Do not upload sensitive or confidential material.</li>
                <li><strong>Accuracy Disclaimer:</strong> While we strive for accuracy, musical conversions are provided "as is" without warranty. Verify all conversions before final use.</li>
                <li><strong>AI-Generated Content:</strong> AI-assisted compositions are generated based on patterns and may require human refinement. You are responsible for final musical arrangements.</li>
                <li><strong>Export Formats:</strong> XML, MXL, and PDF exports are provided for convenience. Compatibility with all notation software is not guaranteed.</li>
                <li><strong>Cultural Respect:</strong> The Tu'ungafasi system is a cherished part of Tongan heritage. Please use this tool with respect for its cultural significance.</li>
              </ol>
              
              <p className="text-sm text-gray-700 mt-6">
                By clicking "Accept & Continue," you acknowledge that you have read, understood, and agree to comply with these terms and conditions.
              </p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={() => { setTermsAccepted(true); setShowTerms(false); }}
              className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Accept & Continue
            </button>
            <button
              onClick={() => window.close()}
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Music className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">Tonga Musika</h1>
                <p className="text-sm text-white/80">Tu'ungafasi Digital Converter</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white/80 text-sm">musika.tongamarket.com</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-2 bg-white/10 backdrop-blur-md rounded-lg p-2">
          {[
            { id: 'convert', label: 'Convert Music', icon: File },
            { id: 'compose', label: 'AI Composer', icon: Wand2 },
            { id: 'about', label: 'About Tu\'ungafasi', icon: AlertCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === tab.id ? 'bg-white text-purple-600 shadow-lg' : 'text-white hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-12">
        {activeTab === 'convert' && (
          <div className="space-y-6">
            {/* File Import Section */}
            <FileImport 
              onMusicDataParsed={handleMusicDataParsed}
              onError={handleImportError}
            />

            {/* Sheet Music Viewer (if imported) */}
            {importedMusicXML && (
              <SheetMusicViewer 
                musicXML={importedMusicXML}
                title={importedMusicData?.title || 'Imported Music'}
              />
            )}

            {/* Conversion Interface */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Panel */}
              <div className="bg-white rounded-2xl shadow-2xl p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Music Input</h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Conversion Mode</label>
                  <select
                    value={conversionMode}
                    onChange={(e) => setConversionMode(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="western-to-tongan">Western → Tongan (Tu'ungafasi)</option>
                    <option value="tongan-to-western">Tongan (Tu'ungafasi) → Western</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Key</label>
                    <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                      {keys.map(key => (<option key={key} value={key}>{key} major</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Time</label>
                    <select value={selectedTimeSignature} onChange={(e) => setSelectedTimeSignature(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                      {timeSignatures.map(ts => (<option key={ts} value={ts}>{ts}</option>))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {conversionMode === 'western-to-tongan' ? 'Western Notation' : 'Tongan Notation'}
                  </label>
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={conversionMode === 'western-to-tongan' 
                      ? 'Enter Western notation (e.g., C4 D4 E4 F4 | G4 A4 B4 C5)'
                      : 'Enter Tongan notation (e.g., 3:4:5:6|7---|)'}
                    className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                  />
                </div>

                <button onClick={handleConvert} className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors">
                  Convert Music
                </button>
              </div>

              {/* Output Panel */}
              <div className="bg-white rounded-2xl shadow-2xl p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Converted Music</h2>
                
                <textarea value={outputText} readOnly placeholder="Converted notation will appear here..." className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm mb-4" />

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Play Voice Parts</label>
                  <div className="grid grid-cols-2 gap-2">
                    {voiceParts.map(part => (
                      <label key={part.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={selectedVoiceParts.includes(part.id)} onChange={(e) => {
                          if (e.target.checked) { setSelectedVoiceParts([...selectedVoiceParts, part.id]); } 
                          else { setSelectedVoiceParts(selectedVoiceParts.filter(p => p !== part.id)); }
                        }} className="w-4 h-4" />
                        <span className={`w-3 h-3 rounded-full ${part.color}`} />
                        <span className="text-sm">{part.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Audio Instrument</label>
                  <select value={audioInstrument} onChange={(e) => setAudioInstrument(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option value="piano">Piano</option>
                    <option value="organ">Organ</option>
                    <option value="vocal">Vocal (Choir)</option>
                  </select>
                </div>

                <div className="flex gap-3">
                  <button onClick={handleAudioPlay} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    {isPlaying ? 'Pause' : 'Play'}
                  </button>
                  
                  <div className="flex-1 flex gap-2">
                    <button onClick={() => handleExport('txt')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Download className="w-5 h-5" />TXT
                    </button>
                    <button onClick={() => handleExport('mxl')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Download className="w-5 h-5" />MXL
                    </button>
                    <button onClick={() => handleExport('pdf')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Download className="w-5 h-5" />PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compose' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">AI-Assisted Song Composer</h2>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Describe Your Song</label>
                <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Example: A joyful hymn in C major, 4/4 time, about God's love and mercy. Include traditional Tongan harmonies with strong bass line." className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Key</label>
                  <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    {keys.map(key => (<option key={key} value={key}>{key} major</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Time</label>
                  <select value={selectedTimeSignature} onChange={(e) => setSelectedTimeSignature(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    {timeSignatures.map(ts => (<option key={ts} value={ts}>{ts}</option>))}
                  </select>
                </div>
              </div>

              <button onClick={handleAIGenerate} disabled={aiGenerating || !aiPrompt.trim()} className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Wand2 className={`w-5 h-5 ${aiGenerating ? 'animate-spin' : ''}`} />
                {aiGenerating ? 'Composing Your Song...' : 'Generate Tongan Song'}
              </button>

              {outputText && (
                <div className="mt-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Generated Composition</h3>
                  <textarea value={outputText} readOnly className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm" />
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleAudioPlay} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                      <Play className="w-5 h-5" />Play Composition
                    </button>
                    <button onClick={() => handleExport('pdf')} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      <Download className="w-5 h-5" />Export as PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Tu'ungafasi Notation</h2>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 mb-4">
                  The Tu'ungafasi system was developed in the 19th century by Shirley Waldemar Baker as a simplified musical notation for Tongan church choirs. It uses numbers 3-9 to represent the do-re-mi-fa-sol-la-ti scale.
                </p>

                <div className="bg-purple-50 rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Scale Degrees</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { num: '3', note: 'Do (Tonic)' }, { num: '4', note: 'Re' }, { num: '5', note: 'Mi' }, { num: '6', note: 'Fa' },
                      { num: '7', note: 'Sol' }, { num: '8', note: 'La' }, { num: '9', note: 'Ti' }
                    ].map(item => (
                      <div key={item.num} className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-purple-600">{item.num}</div>
                        <div className="text-sm text-gray-600">{item.note}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-3">Four Voice Parts</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {voiceParts.map(part => (
                    <div key={part.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                      <div className={`w-4 h-4 rounded-full ${part.color}`} />
                      <div>
                        <div className="font-semibold text-gray-800">{part.label}</div>
                        <div className="text-sm text-gray-600">
                          {part.id === 'fasi' && 'Lead melody, soprano range'}
                          {part.id === 'kanokano' && 'Alto harmony, second voice'}
                          {part.id === 'tenoa' && 'Tenor harmony, third voice'}
                          {part.id === 'laulalo' && 'Bass foundation, lowest voice'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">How to Use This Tool</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Import:</strong> Upload MusicXML files from MuseScore or other notation software</li>
                    <li><strong>Convert:</strong> Automatic conversion between Western and Tongan notation</li>
                    <li><strong>Play:</strong> Listen to your music with synthesized instruments</li>
                    <li><strong>Export:</strong> Download as PDF, MusicXML, or print-ready formats</li>
                    <li><strong>Compose:</strong> Use AI to generate new Tongan hymns</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white/10 backdrop-blur-md border-t border-white/20 py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="text-center text-white/80 text-sm mb-4">
            <p>© 2025 Tonga Musika • Part of tongamarket.com • Made with ❤️ for Pacific Island musical heritage</p>
            <p className="mt-2">100% Open Source • Zero Cost • Self-Funded Development Project</p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/70 text-sm">
            <div className="footer-links flex gap-6">
              <a href="mailto:stevenesau2023@gmail.com" className="hover:text-white transition-colors">📧 Email Support</a>
              <a href="https://wa.me/6767711961" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">📱 WhatsApp: +676 7711961</a>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="credit">Developed by <a href="https://stevenesau.com" target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-white font-semibold">Stevenesau.com</a></div>
              <div className="credit">Powered by <a href="https://tongamarket.com" target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-white font-semibold">TongaMarket.com</a></div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TongaMusikaApp;